import type { RecapDraftOutput } from '../../../../services/api/aiTypes';

/**
 * Recap-email helpers for the `/api/ai/recap-draft` response.
 *
 * 2026-06-11 (coach review, post-MED-972): the AI's only email contribution is
 * `key_themes`, slotted into the admin template's bullet region. The old
 * `composeRecapEmailBody` full-AI-body fallback (greeting / what-worked /
 * commitments / carryover / closing as email prose) is retired — with no
 * template configured the card shows a template-missing notice instead.
 * Commitments and carryover still surface on the post-session screen via
 * `composeSessionThemesText` / `composeCarryoverText` (MED-893 AC7).
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Render `key_themes` as the inner `<li>` items of a bullet list: one item per
 * theme as "{theme} &mdash; {summary}" (both escaped), or just the non-empty
 * part when only one is present. Consumed by `injectKeyThemesIntoTemplate`
 * (MED-972 template wrapper).
 */
function renderKeyThemeListItems(
  themes: RecapDraftOutput['recap_email']['key_themes'],
): string {
  return themes
    .map((kt) => {
      const theme = escapeHtml(kt.theme.trim());
      const summary = escapeHtml(kt.summary.trim());
      if (theme && summary) return `<li>${theme} &mdash; ${summary}</li>`;
      return `<li>${theme || summary}</li>`;
    })
    .join('');
}

/**
 * MED-972 — slot the AI's `key_themes` into the admin template's bullet region.
 *
 * The recap-email default template (E2.6c, `system_configs.recap_email_default_body`)
 * carries exactly one empty bullet list (`<ul><li></li>…</ul>`) as the spot the
 * coach would normally fill by hand. When AI features are active and the recap
 * draft succeeds, we replace that list's contents with one `<li>` per theme —
 * leaving every other line of the template (greeting, resources placeholder,
 * next-session reference, sign-off) exactly as the admin authored it (AC1, AC3).
 *
 * `templateHtml` is expected to already have its merge tags resolved
 * (`resolveTemplateMergeTags`). Returns the template unchanged when there are no
 * themes (consistent with the empty-bullet fail-open template, AC7) or when the
 * template has no `<ul>` to fill (admin removed it) — safe degradation rather
 * than dropping the AI content blindly.
 *
 * The replacement uses the function form of `String.prototype.replace` so theme
 * or summary text containing `$` is inserted literally rather than being
 * interpreted as a `$&`/`$1` replacement pattern.
 */
export function injectKeyThemesIntoTemplate(
  templateHtml: string,
  themes: RecapDraftOutput['recap_email']['key_themes'],
): string {
  if (themes.length === 0) return templateHtml;
  // MED-1187 AC2a — the AI returns 0-4 real bullets; the template always shows
  // at least 3 slots. Top partial content up with empty placeholder bullets
  // (1 AI -> 1 filled + 2 empty; 2 -> 2+1; 3-4 -> no padding) so the coach is
  // prompted to complete the section by hand. Pads are blank slots for a
  // human, never invented content (Ronnie, 2026-07-10).
  const pad = Math.max(0, 3 - themes.length);
  const items = renderKeyThemeListItems(themes) + '<li></li>'.repeat(pad);
  return templateHtml.replace(/<ul>[\s\S]*?<\/ul>/, () => `<ul>${items}</ul>`);
}

/**
 * MED-893 AC7 / MED-988 AC4 — derive the textarea string for `session_insights`
 * from the same recap-draft response (`recap_email.key_themes[]`). Renders one
 * bullet per key_theme as a single textarea value. The feedback form persists
 * this as a plain string in `coach_session_feedback.responses.session_insights`
 * (JSONB). Re-targeted from the former `session_themes` field in MED-988.
 */
export function composeSessionInsightsText(
  themes: RecapDraftOutput['recap_email']['key_themes'],
): string {
  return themes
    .map((kt) => {
      const theme = kt.theme.trim();
      const summary = kt.summary.trim();
      if (theme && summary) return `• ${theme} — ${summary}`;
      return `• ${theme || summary}`;
    })
    .join('\n');
}

/**
 * MED-893 AC7 — array→string conversion for the `carryover_for_next_session`
 * textarea (Jerry flagged 2026-05-28). Storage is a single string; the wire
 * shape is `string[]`. Joined with bullet + newline so the textarea reads
 * naturally and the next session's pre-session-guidance recipe parses it back
 * as a single string.
 */
export function composeCarryoverText(items: string[]): string {
  return items.map((item) => `• ${item.trim()}`).join('\n');
}
